"use client";

import React, { createContext, useContext, useEffect, useMemo, useRef } from "react";
import type Centrifuge from "centrifuge";
import { getCentrifuge } from "@/src/lib/centrifuge";

/**
 * React Context wrapper for the shared Centrifugo singleton.
 *
 * IMPORTANT:
 * - Do NOT create a new Centrifuge instance here (it would open a 2nd websocket).
 * - Do NOT disconnect on unmount (connection lifecycle is owned by useCentrifugoAuth/connectCentrifugo).
 */
interface CentrifugeContextType {
  centrifuge: Centrifuge | null;
  subscribe: (channel: string, onMessage: (data: any) => void) => Centrifuge.Subscription | null;
  unsubscribe: (subscription: Centrifuge.Subscription) => void;
}

const CentrifugeContext = createContext<CentrifugeContextType>({
  centrifuge: null,
  subscribe: () => null,
  unsubscribe: () => { },
});

export const useCentrifuge = () => useContext(CentrifugeContext);

interface CentrifugeProviderProps {
  children: React.ReactNode;
}

export function CentrifugeProvider({ children }: CentrifugeProviderProps) {
  const subscriptionsRef = useRef<Map<string, Centrifuge.Subscription>>(new Map());

  // Initialize singleton reference once for context consumers.
  const centrifuge = useMemo(() => getCentrifuge(), []);

  useEffect(() => {
    return () => {
      // Only cleanup subscriptions created via this provider.
      subscriptionsRef.current.forEach((sub) => {
        try {
          sub.unsubscribe();
        } finally {
          try {
            (sub as any)?.remove?.();
          } catch {
            // ignore
          }
        }
      });
      subscriptionsRef.current.clear();
    };
  }, []);

  const subscribe = (
    channel: string,
    onMessage: (data: any) => void
  ): Centrifuge.Subscription | null => {
    const c = centrifuge ?? getCentrifuge();
    if (!c) {
      console.warn("Centrifuge singleton is not available");
      return null;
    }

    if (subscriptionsRef.current.has(channel)) {
      return subscriptionsRef.current.get(channel) || null;
    }

    const subscription = c.subscribe(channel, {
      publish: (ctx: Centrifuge.PublicationContext) => {
        onMessage(ctx.data);
      },
      subscribe: (ctx: Centrifuge.SubscribeSuccessContext) => {
        console.log("Subscribed to channel:", channel, ctx);
      },
      error: (ctx: Centrifuge.SubscribeErrorContext) => {
        console.error("Subscription error:", channel, ctx);
      },
      unsubscribe: (ctx: Centrifuge.UnsubscribeContext) => {
        console.log("Unsubscribed from channel:", channel, ctx);
      },
    });

    subscription.subscribe();
    subscriptionsRef.current.set(channel, subscription);

    return subscription;
  };

  const unsubscribe = (subscription: Centrifuge.Subscription) => {
    try {
      subscription.unsubscribe();
    } finally {
      try {
        (subscription as any)?.remove?.();
      } catch {
        // ignore
      }
      subscriptionsRef.current.forEach((sub, channel) => {
        if (sub === subscription) subscriptionsRef.current.delete(channel);
      });
    }
  };

  const value = useMemo(
    () => ({
      centrifuge,
      subscribe,
      unsubscribe,
    }),
    [centrifuge]
  );

  return <CentrifugeContext.Provider value={value}>{children}</CentrifugeContext.Provider>;
}
